import {
  AppliedDataFilter,
  DataFilterOperation,
  ProgramType,
  RequirementFlagSettingsDocument,
  RequirementFlagSettingsType,
  RequirementFlagSettingPatchRequest,
  RequirementFlagSettingPostRequest,
} from 'types';

import { api } from './api';

interface RequirementFlagSettingsSearchResponse {
  pageNum: number;
  total: number;
  requirementFlagSettings: RequirementFlagSettingsDocument[];
}

export const requirementFlagSettingsApi = api.injectEndpoints({
  endpoints: (build) => ({
    createRequirementFlagSetting: build.mutation<
      RequirementFlagSettingsDocument,
      { body: RequirementFlagSettingPostRequest }
    >({
      query: ({ body }) => ({
        url: `admin/quality-control/requirement-flag-settings`,
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: 'RequirementFlagSettings', id: 'LIST' }],
    }),
    getRequirementFlagSettings: build.query<RequirementFlagSettingsSearchResponse, Record<any, any>>({
      query: (queryParams) => {
        return {
          url: `quality-control/requirement-flag-settings${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
      keepUnusedDataFor: 300,
      transformResponse: (response: any) => {
        const { data, pageNum, total } = response || {};
        return {
          pageNum,
          total,
          requirementFlagSettings: data?.requirementFlagSettings || [],
        };
      },
      providesTags: (result) =>
        result?.requirementFlagSettings
          ? [
              ...result.requirementFlagSettings.flatMap(({ id, type }) => [
                { type: 'RequirementFlagSettings' as const, id: id?.toString() },
                { type: 'RequirementFlagSettings' as const, id: type },
              ]),
              { type: 'RequirementFlagSettings', id: 'LIST' },
            ]
          : [{ type: 'RequirementFlagSettings', id: 'LIST' }],
    }),
    getRequirementFlagSetting: build.query<RequirementFlagSettingsDocument, { id: string }>({
      query: ({ id }) => ({ url: `quality-control/requirement-flag-settings/${id}` }),
      providesTags: (_result, _error, { id }) => [{ type: 'RequirementFlagSettings', id }],
    }),
    updateRequirementFlagSetting: build.mutation<
      RequirementFlagSettingsDocument,
      { id: string; body: RequirementFlagSettingPatchRequest }
    >({
      query: ({ id, body }) => ({
        url: `admin/quality-control/requirement-flag-settings/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, _error) =>
        result
          ? [
              { type: 'RequirementFlagSettings', id: 'LIST' },
              { type: 'RequirementFlagSettings', id: result.id?.toString() },
              { type: 'RequirementFlagSettings', id: result.type },
            ]
          : [{ type: 'RequirementFlagSettings', id: 'LIST' }],
    }),
    getRequirementFlagSettingListFilters: build.query<any, void>({
      query: () => {
        return {
          url: `admin/quality-control/requirement-flag-settings/filters`,
        };
      },
    }),
  }),
});

export const {
  useCreateRequirementFlagSettingMutation,
  useLazyGetRequirementFlagSettingsQuery,
  useGetRequirementFlagSettingQuery,
  useGetRequirementFlagSettingListFiltersQuery,
  useUpdateRequirementFlagSettingMutation,
} = requirementFlagSettingsApi;

export const useGetRequirementFlagSettingsQuery = (
  {
    type,
    pageNum = 1,
    pageSize = 1000,
    programTypes,
  }: {
    type: RequirementFlagSettingsType | undefined;
    pageNum?: number;
    pageSize?: number;
    programTypes?: ProgramType[];
  },
  options?: { skip?: boolean },
): ReturnType<typeof requirementFlagSettingsApi.endpoints.getRequirementFlagSettings.useQuery> => {
  const advancedFilters: AppliedDataFilter[] = [];
  if (programTypes && programTypes.length > 0) {
    advancedFilters.push({
      id: 'programTypes',
      selectedValues: programTypes,
      operation: DataFilterOperation.Equal,
    });
  }

  const queryArgs: Record<string, any> = { pageNum, pageSize };
  if (advancedFilters.length > 0) {
    queryArgs.advancedFilters = JSON.stringify(advancedFilters);
  }

  return requirementFlagSettingsApi.endpoints.getRequirementFlagSettings.useQuery(queryArgs, {
    ...options,
    selectFromResult: (queryState) => {
      const { data, ...rest } = queryState;
      if (!data) {
        return { data: undefined, ...rest };
      }

      let filteredData = [];
      if (type) {
        filteredData = [data?.requirementFlagSettings?.filter((setting) => setting.type === type) ?? []].flat();
      } else {
        filteredData = data.requirementFlagSettings;
      }

      return {
        data: {
          pageNum: data.pageNum,
          total: filteredData.length,
          requirementFlagSettings: filteredData,
        },
        ...rest,
      };
    },
  });
};
